import { useEffect, useRef, useState } from 'react';

import { formatTime } from './format-time';
import type { AudioPlayback } from './use-audio-playback';
import type { AudioFileViewerSize } from '../audio-file-viewer';
import type { AudioWaveform as AudioWaveformData } from '@/api/types/backend-dto-types';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';

interface AudioWaveformProps {
  waveform: AudioWaveformData;
  duration: number;
  playback: AudioPlayback;
  onRenderError: () => void;
  isDisabled?: boolean;
  size?: AudioFileViewerSize;
}

export function AudioWaveform({
  waveform,
  duration,
  playback,
  onRenderError,
  isDisabled,
  size = 'default',
}: AudioWaveformProps) {
  const { audioRef, currentTime, setCurrentTime, seekTo } = playback;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    const container = containerRef.current;
    if (!audio || !container) return;

    let isCancelled = false;
    let destroyWaveform: (() => void) | undefined;

    setIsReady(false);

    void import('wavesurfer.js')
      .then(({ default: WaveSurfer }) => {
        if (isCancelled) return;

        const wavesurfer = WaveSurfer.create({
          container,
          media: audio,
          peaks: [waveform.peaks.flat()],
          duration,
          height: size === 'sm' ? 20 : 26,
          waveColor: '#d1d5db',
          progressColor: '#374151',
          cursorColor: '#9ca3af',
          cursorWidth: 2,
          barWidth: 2,
          barGap: 2,
          barRadius: 4,
          barMinHeight: 2,
          dragToSeek: true,
          normalize: true,
        });

        wavesurfer.on('redrawcomplete', () => setIsReady(true));
        wavesurfer.on('interaction', (time) => setCurrentTime(time));
        wavesurfer.on('error', () => {
          // Wavesurfer re-emits media errors, which the playback hook already recovers from.
          if (!audio.error) onRenderError();
        });
        destroyWaveform = () => wavesurfer.destroy();
      })
      .catch(() => {
        if (!isCancelled) onRenderError();
      });

    return () => {
      isCancelled = true;
      destroyWaveform?.();
    };
  }, [audioRef, waveform, duration, setCurrentTime, onRenderError, size]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const seekAmount = event.shiftKey ? 10 : 5;
    let nextTime: number | null = null;

    if (event.key === 'ArrowLeft') nextTime = currentTime - seekAmount;
    if (event.key === 'ArrowRight') nextTime = currentTime + seekAmount;
    if (event.key === 'Home') nextTime = 0;
    if (event.key === 'End') nextTime = duration;

    if (nextTime === null) return;
    event.preventDefault();
    seekTo(Math.max(0, Math.min(nextTime, duration)));
  };

  return (
    <div
      className={cn(
        'relative min-w-0 flex-1 focus:outline-none',
        size === 'sm' ? 'h-5' : 'h-6.5',
        isDisabled && 'pointer-events-none',
      )}
      role="slider"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      aria-label="Audio position"
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={Math.min(currentTime, duration)}
      aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
      onKeyDown={isDisabled ? undefined : handleKeyDown}
    >
      <div
        ref={containerRef}
        className={cn(size === 'sm' ? 'h-5' : 'h-6.5', 'cursor-pointer overflow-hidden', !isReady && 'opacity-0')}
      />
      {!isReady && <Skeleton className={cn('absolute inset-0 w-full', size === 'sm' ? 'h-5' : 'h-6.5')} />}
    </div>
  );
}
